import { useState, useRef, useEffect, type ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { appendAuthHandoff } from '../../lib/handoff';

export interface AppSwitcherApp {
  id: string;
  name: string;
  description?: string;
  /** Icon displayed in the dropdown row (small) */
  icon?: ReactNode;
  url: string;
}

export interface AppSwitcherProps {
  /** Logo / branding element acting as the dropdown trigger (usually the current app logo) */
  trigger: ReactNode;
  apps: AppSwitcherApp[];
  currentAppId: string;
  className?: string;
}

/**
 * AppSwitcher — wraps the current app logo as a clickable trigger that
 * opens a dropdown listing all available apps. Clicking another app navigates
 * to its URL (with optional token passthrough for cross-domain setups).
 */
export function AppSwitcher({
  trigger,
  apps,
  currentAppId,
  className,
}: AppSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSwitch = (app: AppSwitcherApp) => {
    if (app.id === currentAppId) {
      setIsOpen(false);
      return;
    }
    window.location.href = appendAuthHandoff(app.url);
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        title="Changer d'application"
        className={cn(
          'flex items-center gap-1.5 rounded-md p-1 -m-1',
          'hover:bg-sidebar-hover transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400'
        )}
      >
        {trigger}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-sidebar-icon transition-transform duration-150',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          className={cn(
            'absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-lg',
            'border border-neutral-200 bg-white shadow-xl'
          )}
        >
          <div className="border-b border-neutral-100 px-3 py-2">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Applications
            </p>
          </div>
          <ul className="py-1">
            {apps.map((app) => {
              const isCurrent = app.id === currentAppId;
              return (
                <li key={app.id}>
                  <button
                    type="button"
                    onClick={() => handleSwitch(app)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors',
                      'hover:bg-neutral-50',
                      isCurrent && 'bg-primary-50'
                    )}
                  >
                    {app.icon && (
                      <span
                        className={cn(
                          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
                          isCurrent
                            ? 'bg-primary-100 text-primary-600'
                            : 'bg-neutral-100 text-neutral-600'
                        )}
                      >
                        {app.icon}
                      </span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-dark-500">
                        {app.name}
                      </p>
                      {app.description && (
                        <p className="truncate text-xs text-neutral-500">
                          {app.description}
                        </p>
                      )}
                    </div>
                    {isCurrent && (
                      <Check className="h-4 w-4 flex-shrink-0 text-primary-600" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
